// Writing family-app records end-to-end encrypted (M28 part 2, D65). With
// this device's key everything is sealed with the family key here; without
// it, writes go out as before (and the server refuses them once the family's
// apps are encrypted). Files are never uploaded unencrypted into an encrypted
// family (addDocumentFile).
import { api, type FamilyDocument, type ListTemplate } from '../api';
import { sealedPreviewFor } from '../previewImage';
import type { FileSecret } from './crypto';
import { ensurePlainUploadAllowed } from './plainUpload';
import { uploadSealed } from './send';
import {
  isE2EEReady,
  sealApp,
  isLockedApp,
  writeSealedApp,
  type OpenedApp,
} from './session';

export { isE2EEReady, isLockedApp };

/**
 * A new record's id, chosen here so its v2 seal is bound to it before the
 * server has seen it (M76, D329).
 */
export const newRecordId = (): string => crypto.randomUUID();

/** A record this device can't open can't be sealed again for another. */
export class RecordsLockedError extends Error {
  constructor() {
    super('E2EE_RECORDS_LOCKED');
  }
}

type ItemEnvelope = { text: string; note: string | null };

/** What a list item or template item holds, opened, or null if locked. */
function itemEnvelope(item: {
  sealed?: string | null;
  e2eeApp?: OpenedApp;
  text: string;
  note?: string | null;
}): ItemEnvelope | null {
  if (item.sealed) {
    const opened = item.e2eeApp?.envelope as ItemEnvelope | undefined;
    return opened && !item.e2eeApp?.unverified ? opened : null;
  }
  return { text: item.text, note: item.note ?? null };
}

/**
 * Each item sealed for a fresh id of its own and the record it belongs to
 * (D329): a list's items as `item`, a template's as `titem`.
 */
function sealItems(
  familyId: string,
  envelopes: ItemEnvelope[],
  kind: 'item' | 'titem',
  parentId: string
) {
  return Promise.all(
    envelopes.map(async (envelope) => {
      const id = newRecordId();
      return {
        id,
        sealed: await sealApp(familyId, kind, envelope, id, parentId),
      };
    })
  );
}

/**
 * A new sealed list, from a template or not. A template's items are opened
 * here and sealed again for the new list's item ids (D329), so the server
 * never copies sealed items between records.
 */
export function createSealedList(
  familyId: string,
  name: string,
  template?: ListTemplate
) {
  const id = newRecordId();
  const envelopes = (template?.items ?? []).map(itemEnvelope);
  if (envelopes.some((envelope) => !envelope)) {
    return Promise.reject(new RecordsLockedError());
  }
  return writeSealedApp(
    familyId,
    'name',
    { name },
    async (sealed) =>
      api.createFamilyList(
        familyId,
        {
          sealed,
          id,
          ...(template
            ? {
                items: await sealItems(
                  familyId,
                  envelopes as ItemEnvelope[],
                  'item',
                  id
                ),
              }
            : {}),
        },
        template?.id
      ),
    id
  );
}

/**
 * Keeps a sealed list as a template (D48): its name and items opened here
 * and sealed again for the template and ids of its own (D329). A list from
 * before encryption goes as before.
 */
export async function saveSealedTemplate(familyId: string, listId: string) {
  const { data } = await api.getFamilyList(familyId, listId);
  if (!data.sealed || !isE2EEReady()) {
    return api.saveListTemplate(familyId, listId);
  }
  const name = data.e2eeApp?.envelope as { name?: string } | undefined;
  const envelopes = [...data.items]
    .sort((a, b) => a.position - b.position)
    .map(itemEnvelope);
  if (
    !name?.name ||
    data.e2eeApp?.unverified ||
    envelopes.some((envelope) => !envelope)
  ) {
    throw new RecordsLockedError();
  }
  const id = newRecordId();
  // A template's name and items have kinds of their own (D329).
  return writeSealedApp(
    familyId,
    'tname',
    { name: name.name },
    async (sealed) =>
      api.saveListTemplate(familyId, listId, {
        id,
        sealed,
        items: await sealItems(
          familyId,
          envelopes as ItemEnvelope[],
          'titem',
          id
        ),
      }),
    id
  );
}

/** What an encrypted document carries (and an opened one exposes). */
export interface DocumentEnvelope {
  fileName: string;
  mimeType: string;
  fileSize: number;
  description?: string | null;
  folder?: string | null;
  tags?: string[];
  file?: FileSecret;
  thumb?: FileSecret;
  /** The file itself was never encrypted (saved from an older message). */
  plainFile?: boolean;
}

/** The secrets an opened document carries for its file. */
export function documentSecret(document: { e2eeApp?: OpenedApp }): {
  file?: FileSecret;
  mimeType?: string;
} {
  const envelope = document.e2eeApp?.envelope as DocumentEnvelope | undefined;
  if (!envelope || envelope.plainFile) return {};
  return { file: envelope.file, mimeType: envelope.mimeType };
}

/** Saves a link to the family's Links, encrypted (address, preview, note). */
export function saveSealedLink(
  familyId: string,
  input: { url: string; note?: string; messageId?: string }
) {
  return (async () => {
    // The picture is a sealed copy, never the site's address (D318).
    const preview = await sealedPreviewFor(input.url);
    const id = newRecordId();
    return writeSealedApp(
      familyId,
      'link',
      {
        url: input.url,
        note: input.note ?? null,
        title: preview?.title ?? null,
        description: preview?.description ?? null,
        imageUrl: null,
        siteName: preview?.siteName ?? null,
        ...(preview?.image ? { image: preview.image } : {}),
      },
      (sealed) =>
        api.addFamilyLink(familyId, {
          sealed,
          id,
          ...(input.messageId ? { messageId: input.messageId } : {}),
        }),
      id
    );
  })();
}

/** Uploads a file to Documents, encrypted on this device first. */
export async function uploadSealedDocument(familyId: string, file: File) {
  const { upload, file: secret } = await uploadSealed(file, undefined, {
    scope: { familyId },
  });
  const id = newRecordId();
  return writeSealedApp<{ success: boolean; data: FamilyDocument }>(
    familyId,
    'doc',
    {
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      fileSize: file.size,
      file: secret,
    } satisfies DocumentEnvelope,
    (sealed) =>
      api.addFamilyDocument(familyId, {
        sealed,
        id,
        attachmentUrl: upload.url,
      }),
    id
  );
}

/**
 * Adds a picked file to Documents: sealed with this device's key, or, without
 * one yet, unencrypted only for a family whose apps aren't encrypted (M28).
 * Rejects with KEYS_NOT_READY before uploading anything otherwise.
 */
export async function addDocumentFile(familyId: string, file: File) {
  if (isE2EEReady()) return uploadSealedDocument(familyId, file);
  await ensurePlainUploadAllowed('family', familyId);
  return api.addFamilyDocument(familyId, {
    attachment: await api.uploadAttachment(file, file.name, {}, undefined, {
      scope: { familyId },
    }),
  });
}

/** Saves a chat attachment to Documents; its file isn't copied (D29). */
export function saveSealedMessageDocument(
  familyId: string,
  messageId: string,
  details: DocumentEnvelope
) {
  const id = newRecordId();
  return writeSealedApp<{ success: boolean; data: FamilyDocument }>(
    familyId,
    'doc',
    details,
    (sealed) => api.addFamilyDocument(familyId, { sealed, id, messageId }),
    id
  );
}

/** New folder, tags or note for a document: its whole envelope again. */
export function updateSealedDocument(
  familyId: string,
  documentId: string,
  details: DocumentEnvelope
) {
  return writeSealedApp<{ success: boolean; data: FamilyDocument }>(
    familyId,
    'doc',
    details,
    (sealed) => api.updateFamilyDocument(familyId, documentId, { sealed }),
    documentId
  );
}

/**
 * Every page of a family-app list. With encryption the server can't search
 * or filter, so the device loads everything and does it (a family's worth).
 */
export async function fetchAllPages<T>(
  fetchPage: (
    cursor?: string
  ) => Promise<{ data: T[]; pagination: { hasMore: boolean; cursor?: string } }>
): Promise<T[]> {
  const all: T[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < 40; page += 1) {
    const response = await fetchPage(cursor);
    all.push(...response.data);
    if (!response.pagination.hasMore || !response.pagination.cursor) break;
    cursor = response.pagination.cursor;
  }
  return all;
}

/** "https://Example.com/a/" and "https://example.com/a" are the same link. */
export function sameLink(a: string, b: string): boolean {
  const norm = (value: string) => {
    try {
      const url = new URL(value.trim());
      return `${url.protocol}//${url.host.toLowerCase()}${url.pathname.replace(/\/$/, '')}${url.search}`;
    } catch {
      return value.trim().toLowerCase();
    }
  };
  return norm(a) === norm(b);
}

/** Every word of the query is somewhere in these fields. */
export function matchesQuery(
  query: string,
  fields: Array<string | null | undefined>
) {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const haystack = fields.filter(Boolean).join('\n').toLowerCase();
  return terms.every((term) => haystack.includes(term));
}
